import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Alert, TextField, Grid, Link, Typography, useTheme } from '@emburse/embark-core';
import VerticallyCenteredLayout from '../../components/Layouts/VerticallyCenteredLayout';
import { AuthCard, InputPassword } from '../../components';
import { $t, useTranslation } from '../../hooks/useTranslation';
export var AuthAttemptStatuses;
(function (AuthAttemptStatuses) {
    AuthAttemptStatuses["Success"] = "Success";
    AuthAttemptStatuses["Fail"] = "Fail";
    AuthAttemptStatuses["AccountDisabled"] = "AccountDisabled";
    AuthAttemptStatuses["AccountTemporarilyLocked"] = "AccountTemporarilyLocked";
    // InvalidUsername, these are not exposed to the client for security reasons
    // InvalidPassword,
    // MissingCredentials,
    AuthAttemptStatuses["SSORequired"] = "SSORequired";
    AuthAttemptStatuses["NotAdmin"] = "NotAdmin";
})(AuthAttemptStatuses || (AuthAttemptStatuses = {}));
export var SystemStatuses;
(function (SystemStatuses) {
    SystemStatuses["spsOperational"] = "spsOperational";
    SystemStatuses["spsDegraded"] = "spsDegraded";
    SystemStatuses["spsPartialOutage"] = "spsPartialOutage";
    SystemStatuses["spsMajorOutage"] = "spsMajorOutage";
})(SystemStatuses || (SystemStatuses = {}));
const params = new URLSearchParams(window.location.search);
export default function Login({ Username, AuthAttemptStatus, SystemStatus }) {
    useTranslation();
    const theme = useTheme();
    const [statusPrefix, setStatusPrefix] = useState('');
    const [statusText, setStatusText] = useState('Happy and healthy');
    const [statusColor, setStatusColor] = useState(theme.palette.success.main);
    const [failedAuthMessage, setFailedAuthMessage] = useState('');
    const [loginTitle, setloginTitle] = useState('Account Login');
    const [loginDesc, setloginDesc] = useState('');
    // load failed auth message
    useEffect(() => {
        switch (AuthAttemptStatus) {
            case AuthAttemptStatuses.Fail:
                setFailedAuthMessage($t('The email address or password you entered is incorrect.'));
                break;
            case AuthAttemptStatuses.AccountDisabled:
                setFailedAuthMessage($t('Your account is disabled. Please contact your system administrator for further assistance.'));
                break;
            case AuthAttemptStatuses.AccountTemporarilyLocked:
                setFailedAuthMessage($t('Your account is disabled for 30 minutes due to excessive failed login attempts.'));
                break;
            case AuthAttemptStatuses.SSORequired:
                setFailedAuthMessage($t(`
                    Your account requires Single Sign-On to access Certify.
                    Please login through your Identity Provider or contact your system administrator for further assistance.
                `));
                break;
            case AuthAttemptStatuses.NotAdmin:
                setFailedAuthMessage($t(`
                    Credentials passed, however your account does not have the required permissions to complete this action.
                    Please login using an account with Full Administrator permissions.
                `));
                break;
            default:
                break;
        }
    }, [AuthAttemptStatus]);
    // load system status
    useEffect(() => {
        switch (SystemStatus) {
            case SystemStatuses.spsOperational:
                setStatusPrefix($t('System Status: '));
                setStatusText($t('Happy and healthy'));
                setStatusColor(theme.palette.success.main);
                break;
            case SystemStatuses.spsDegraded:
                setStatusPrefix('');
                setStatusText($t('Experiencing performance issues'));
                setStatusColor(theme.palette.warning.main);
                break;
            case SystemStatuses.spsPartialOutage:
                setStatusPrefix('');
                setStatusText($t('Experiencing component problems'));
                setStatusColor('#FF7F3E');
                break;
            case SystemStatuses.spsMajorOutage:
                setStatusPrefix('');
                setStatusText($t('Some components are down'));
                setStatusColor(theme.palette.error.main);
                break;
            default:
                break;
        }
    }, [
        SystemStatus,
        theme.palette.error.main,
        theme.palette.success.main,
        theme.palette.warning.main
    ]);
    // load alt headers
    useEffect(() => {
        if (params.get('provider') === 'travelperk') {
            setloginTitle('Authorize TravelPerk Integration');
            setloginDesc('Sign in using your Certify Administrator credentials to authorize a connection to TravelPerk.');
        }
    }, []);
    const showPasswordResetAlert = Boolean(params.get('reset')?.length);
    if (showPasswordResetAlert) {
        window.history.replaceState(null, document.title, window.location.pathname);
    }
    return (_jsx(VerticallyCenteredLayout, { children: _jsx(AuthCard, { id: "Login", titleVariant: "h4", titleText: loginTitle, description: loginDesc, buttonLabel: "Login", buttonValue: "Login", children: _jsxs(Grid, { container: true, spacing: 3, direction: "column", alignItems: "stretch", children: [_jsxs(Grid, { item: true, style: { textAlign: 'center' }, children: [statusPrefix && (_jsx(Typography, { display: "inline", style: { marginRight: '12px' }, children: statusPrefix })), _jsx(Typography, { display: "inline", children: _jsxs(Link, { href: "http://status.certify.com", title: $t('View System Status Details'), target: "_blank", children: [_jsx("span", { style: {
                                                display: 'inline-block',
                                                marginRight: '12px',
                                                padding: '5px 5px 5px 5px',
                                                borderRadius: '100%',
                                                backgroundColor: statusColor
                                            } }), statusText] }) })] }), showPasswordResetAlert && (_jsx(Grid, { item: true, children: _jsx(Alert, { severity: "success", children: $t('Your password has been reset successfully') }) })), failedAuthMessage.length > 0 && (_jsx(Grid, { item: true, children: _jsx(Alert, { severity: "error", children: failedAuthMessage }) })), _jsx(Grid, { item: true, children: _jsx(TextField, { id: "D53DFAFC76FEBB307AEA38378D6881DA", name: "D53DFAFC76FEBB307AEA38378D6881DA", fullWidth: true, defaultValue: Username || '', label: $t('Email'), type: "email", autoFocus: true }) }), _jsx(Grid, { item: true, children: _jsx(InputPassword, { id: "7CDB5079534DF37CD06C4E76D141534F", label: $t('Password') }) }), _jsx(Grid, { item: true, children: _jsx(Typography, { align: "center", children: _jsx(Link, { href: "PasswordAssistance.aspx", children: $t('Recover Your Password') }) }) })] }) }) }));
}
Login.defaultProps = {
    AuthAttemptStatus: undefined
};
